import { Card, Progress, Select, Spinner, Tooltip } from "flowbite-react";
import { useEffect, useState, type FC } from "react";
import { HiInformationCircle } from "react-icons/hi";
import type { IconType } from "react-icons";
import type { MostPlayedItem, MostPlayedRange } from "../types/mostPlayed";
import { isCancelReq } from "../util/axios";

const RANGE_OPTIONS: { value: MostPlayedRange; label: string }[] = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "1y", label: "Last year" },
  { value: "all", label: "All time" },
];

const DEFAULT_RANGE: MostPlayedRange = "30d";

// The "sm" size drops the right padding that keeps the text clear of the arrow
const SELECT_THEME = {
  field: { select: { sizes: { sm: "py-2 pl-2 pr-8 sm:text-xs" } } },
};

interface MostPlayedCardProps {
  title: string;
  icon: IconType;
  /** Explains how plays are counted, shown in a tooltip next to the title */
  info?: string;
  /**
   * Loads the ranked items (most played first) for the selected range. Must be
   * a stable reference (module level function or memoized), it re-runs the
   * request when it changes.
   */
  fetchItems: (range: MostPlayedRange) => Promise<MostPlayedItem[]>;
  emptyMessage?: string;
}

const MostPlayedCard: FC<MostPlayedCardProps> = function ({
  title,
  icon: Icon,
  info,
  fetchItems,
  emptyMessage = "No plays in this period.",
}) {
  const [range, setRange] = useState<MostPlayedRange>(DEFAULT_RANGE);
  const [items, setItems] = useState<MostPlayedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // Ignore a response when the range changed again before it arrived
    let stale = false;
    setLoading(true);
    setFailed(false);

    fetchItems(range)
      .then((result) => {
        if (!stale) setItems(result ?? []);
      })
      .catch((error: any) => {
        if (stale || isCancelReq(error)) return;
        console.log(error);
        setItems([]);
        setFailed(true);
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });

    return () => {
      stale = true;
    };
  }, [range, fetchItems]);

  const topPlays = items[0]?.plays ?? 0;

  return (
    <Card className="border-0 shadow-sm dark:bg-primary-200 dark:text-primary-800">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-indigo-500 p-3">
            <Icon className="size-6 text-white" aria-hidden="true" />
          </div>
          <div className="flex items-center gap-1.5">
            <h4 className="text-base font-semibold text-gray-900">{title}</h4>
            {info && (
              <Tooltip content={info} placement="bottom" className="max-w-xs">
                <button
                  type="button"
                  aria-label={`How ${title} is calculated`}
                  className="flex rounded-full text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-gray-500 dark:hover:text-gray-700"
                >
                  <HiInformationCircle
                    className="size-4.5"
                    aria-hidden="true"
                  />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
        <Select
          aria-label={`${title} time range`}
          sizing="sm"
          theme={SELECT_THEME}
          value={range}
          onChange={(e) => setRange(e.target.value as MostPlayedRange)}
        >
          {RANGE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </div>

      <div className="min-h-60">
        {loading ? (
          <div className="flex h-60 items-center justify-center">
            <Spinner color="info" size="lg" aria-label={`Loading ${title}`} />
          </div>
        ) : failed ? (
          <p className="flex h-60 items-center justify-center text-sm text-red-600">
            Couldn't load this data. Try again later.
          </p>
        ) : items.length === 0 ? (
          <p className="flex h-60 items-center justify-center text-sm text-gray-500">
            {emptyMessage}
          </p>
        ) : (
          <ol className="flex flex-col gap-4">
            {items.map((item, index) => (
              <li key={item.id}>
                <div className="flex items-baseline justify-between gap-4">
                  <p className="flex min-w-0 items-center text-sm font-medium text-gray-900">
                    <span className="mr-3 w-4 shrink-0 text-gray-500">
                      {index + 1}
                    </span>
                    <span className="truncate">{item.name}</span>
                  </p>
                  <p className="shrink-0 text-sm font-semibold text-gray-900">
                    {item.plays.toLocaleString()}
                    <span className="ml-1 font-normal text-gray-500">
                      plays
                    </span>
                  </p>
                </div>
                <Progress
                  progress={topPlays ? (item.plays / topPlays) * 100 : 0}
                  size="sm"
                  color="indigo"
                  className="mt-2 dark:bg-primary-100"
                  aria-label={`${item.name}: ${item.plays} plays`}
                />
              </li>
            ))}
          </ol>
        )}
      </div>
    </Card>
  );
};

export default MostPlayedCard;
